/**
 * PATH: packages/ui/src/kanban/meetingSession.ts
 * FILE_ID: UI-KANBAN-MEETING-SESSION-001
 * PURPOSE: Module-level singleton that OWNS a live board meeting — recorder
 *   engine, incremental transcript polling, rolling AI-analyse cadence,
 *   accept/dismiss of suggestions, refresh-resume, and the beforeunload
 *   guard while recording. React binds to it via useSyncExternalStore
 *   (see hooks/useMeetingMode.ts).
 * WHY: BUG-2026-07-02-MEETING-RECORDING-DIES-ON-NAV (P0, ARC-7 MR1) — the
 *   meeting lifecycle used to be React state inside MeetingModePanel /
 *   useMeetingMode, so ANY route/board navigation unmounted the panel and
 *   cancelled the recording, and a refresh lost the meeting entirely.
 *   INVARIANT: a live recording is owned by this module singleton and
 *   survives every React unmount; only an explicit stop() (End button),
 *   a permanent transcription failure, or the server ending the meeting
 *   stops it. A same-tab refresh resumes the live meeting via the
 *   sessionStorage handoff (meetingSessionPersistence.ts).
 * FLOW:
 *   start()  → resume persisted live meeting OR api.startMeeting →
 *              recorder.start + poll timer + analyze timer + persist
 *   recorder chunk → api.ingestMeetingSegment (poll surfaces it)
 *   poll     → api.getMeetingLive(afterSeq) → merge segments, suggestions,
 *              mentionedCardIds
 *   stop()   → recorder.stop → api.stopMeeting → brief diarization polling →
 *              clear persistence
 * DEPENDENCIES:
 *   - meetingRecorderEngine (chunked mic capture)
 *   - meetingSessionPersistence (refresh handoff + beforeunload guard)
 *   - KanbanApi meeting methods (bound per hook render — latest wins)
 * RELATED:
 *   - hooks/useMeetingMode.ts: thin React binding (public API unchanged)
 *   - components/board-view/useBoardUiPrefs.ts: seeds meetingOpen from
 *     hasActiveOrResumableMeeting() so the panel re-appears after nav/refresh
 */
import type { KanbanApi } from './api'
import type {
  BoardMeeting,
  MeetingSegment,
  MeetingSuggestion,
} from './meetingTypes'
import {
  createMeetingRecorder,
  isRecorderSupported,
  type MeetingRecorderEngine,
} from './meetingRecorderEngine'
import {
  bindBeforeUnloadGuard,
  clearPersistedMeeting,
  persistMeeting,
  readPersistedMeeting,
} from './meetingSessionPersistence'

export type MeetingModeStatus = 'idle' | 'starting' | 'live' | 'ended'

const POLL_MS = 3_000
const ANALYZE_MS = 14_000

/** Transcription failures that are PERMANENT for this server/session — they
 *  recur on EVERY chunk (no provider configured, or the model could not
 *  load). When one fires we stop recording instead of looping a doomed upload
 *  forever, release the mic, and surface a banner. (Console-strobe fix
 *  2026-06-22 — the recorder previously retried silently every chunk.) */
const PERMANENT_TRANSCRIBE_ERRORS = new Set<string>([
  'transcription_unconfigured',
  'transcription_unavailable',
])

export interface MeetingSessionState {
  status: MeetingModeStatus
  meeting: BoardMeeting | null
  segments: MeetingSegment[]
  suggestions: MeetingSuggestion[]
  mentionedCardIds: string[]
  isRecording: boolean
  isTranscribing: boolean
  error: string | null
  /** Board the LIVE meeting belongs to (may differ from the visible board). */
  boardId: string | null
}

export interface MeetingStartArgs {
  boardId: string
  language?: string
  pollMs?: number
  analyzeMs?: number
}

const IDLE_STATE: MeetingSessionState = {
  status: 'idle',
  meeting: null,
  segments: [],
  suggestions: [],
  mentionedCardIds: [],
  isRecording: false,
  isTranscribing: false,
  error: null,
  boardId: null,
}

class MeetingSession {
  private state: MeetingSessionState = IDLE_STATE
  private listeners = new Set<() => void>()
  private api: KanbanApi | null = null

  private meetingId: string | null = null
  private recorder: MeetingRecorderEngine | null = null
  private afterSeq = 0
  // BUG-2026-06-29-MEETING-ACCEPT-DISMISS-REAPPEAR — ids the operator has
  // already accepted/dismissed locally. The poll loop overwrites `suggestions`
  // wholesale from the server payload, so a decided card would FLICKER BACK
  // until the backend stopped returning it. We hold decided ids and filter
  // them out of every polled payload, pruning an id only once the server has
  // dropped it (confirmed committed) so the set can't grow unbounded.
  private decidedIds = new Set<string>()
  // FEAT card-context signal (D2 2026-07-06): the card whose drawer is
  // currently open in this tab. Set by CardDetailDrawer, stamped onto every
  // ingested segment so the backend AI attaches unresolved actions to it.
  private contextCardId: string | null = null
  private pollInFlight = false
  private analyzeInFlight = false
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private analyzeTimer: ReturnType<typeof setInterval> | null = null
  private postStopTimeout: ReturnType<typeof setTimeout> | null = null
  private pollMs = POLL_MS
  private analyzeMs = ANALYZE_MS
  private language: string | undefined
  private unbindBeforeUnload: (() => void) | null = null

  // ---- store plumbing (useSyncExternalStore contract) ----

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  getState = (): MeetingSessionState => this.state

  private set(patch: Partial<MeetingSessionState>) {
    this.state = { ...this.state, ...patch }
    for (const fn of this.listeners) fn()
  }

  /** Latest api wins — bound on every useMeetingMode render. */
  bind(api: KanbanApi) {
    this.api = api
  }

  /** True while a meeting is live/starting in THIS tab's JS context. */
  isActive(): boolean {
    return this.state.status === 'live' || this.state.status === 'starting'
  }

  private guardBeforeUnload() {
    if (this.unbindBeforeUnload) return
    this.unbindBeforeUnload = bindBeforeUnloadGuard(() => this.state.isRecording)
  }

  private unguardBeforeUnload() {
    this.unbindBeforeUnload?.()
    this.unbindBeforeUnload = null
  }

  // ---- timers ----

  private clearTimers() {
    if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
    if (this.analyzeTimer) { clearInterval(this.analyzeTimer); this.analyzeTimer = null }
    if (this.postStopTimeout) { clearTimeout(this.postStopTimeout); this.postStopTimeout = null }
  }

  private startTimers() {
    this.clearTimers()
    this.pollTimer = setInterval(() => { void this.poll() }, this.pollMs)
    this.analyzeTimer = setInterval(() => { void this.runAnalyze() }, this.analyzeMs)
  }

  // ---- polling + analyse (ported verbatim from the former useMeetingMode) ----

  private poll = async () => {
    const id = this.meetingId
    const api = this.api
    if (!id || !api?.getMeetingLive || this.pollInFlight) return
    this.pollInFlight = true
    try {
      const payload = await api.getMeetingLive(id, this.afterSeq)
      let segments = this.state.segments
      if (payload.segments.length > 0) {
        const seen = new Set(segments.map((s) => s.id))
        const merged = [...segments]
        for (const seg of payload.segments) {
          if (!seen.has(seg.id)) merged.push(seg)
        }
        segments = merged
        this.afterSeq = Math.max(this.afterSeq, payload.meeting.latest_seq)
      }
      // Prune decided ids the server has already dropped (commit confirmed),
      // then hide any still-decided id so an accept/dismiss isn't undone by a
      // poll racing the commit (BUG-2026-06-29-MEETING-ACCEPT-DISMISS-REAPPEAR).
      if (this.decidedIds.size > 0) {
        const present = new Set(payload.suggestions.map((s) => s.id))
        for (const did of this.decidedIds) {
          if (!present.has(did)) this.decidedIds.delete(did)
        }
      }
      const suggestions =
        this.decidedIds.size > 0
          ? payload.suggestions.filter((s) => !this.decidedIds.has(s.id))
          : payload.suggestions
      let meeting = this.state.meeting
      if (meeting && meeting.id === payload.meeting.id) {
        meeting = {
          ...meeting,
          status: payload.meeting.status,
          ended_at: payload.meeting.ended_at,
          diarization_status: payload.meeting.diarization_status,
          diarized_transcript_text: payload.meeting.diarized_transcript_text,
          diarization_error: payload.meeting.diarization_error,
        }
      }
      this.set({
        segments,
        suggestions,
        mentionedCardIds: payload.mentioned_card_ids,
        meeting,
      })
      // BUG-2026-07-06-MEETING-REMOTE-STOP-IGNORED: the server is the
      // meeting's source of truth. If it reports ended (Stop clicked in
      // another tab/device, or the stale sweep auto-ended a zombie) while
      // THIS tab still thinks it's live, release the mic and wind down —
      // otherwise the recording tab keeps capturing into an ended meeting
      // and "clicking stop doesn't stop it".
      if (
        payload.meeting.status === 'ended' &&
        this.state.status === 'live'
      ) {
        this.handleRemoteEnd()
      }
    } catch (e) {
      // Transient poll failures are non-fatal — keep the loop alive.
      this.set({ error: e instanceof Error ? e.message : 'poll_failed' })
    } finally {
      this.pollInFlight = false
    }
  }

  private runAnalyze = async () => {
    const id = this.meetingId
    const api = this.api
    if (!id || !api?.analyzeMeeting || this.analyzeInFlight) return
    this.analyzeInFlight = true
    try {
      await api.analyzeMeeting(id)
      // New suggestions are PENDING and surface on the next poll; trigger
      // one now so the panel updates promptly after an analyse pass.
      await this.poll()
    } catch {
      // Swallow — analysis is best-effort; the next tick retries.
    } finally {
      this.analyzeInFlight = false
    }
  }

  // ---- recorder wiring ----

  /**
   * Card-context signal: the drawer sets/clears the card it has open.
   * `null` clears (drawer closed). Only affects segments ingested AFTER
   * the call — the backend validates the id against the meeting's board.
   */
  setContextCard(cardId: string | null) {
    this.contextCardId = cardId
  }

  private handleSegmentText = (text: string) => {
    const id = this.meetingId
    const api = this.api
    if (!id || !api?.ingestMeetingSegment) return
    const ctx = this.contextCardId
    void api
      .ingestMeetingSegment(id, ctx ? { text, context_card_id: ctx } : { text })
      .then(() => this.poll())
      .catch((e) => this.set({ error: e instanceof Error ? e.message : 'segment_failed' }))
  }

  private handleAudioChunk = (blob: Blob, seq: number, filename: string) => {
    const id = this.meetingId
    const api = this.api
    if (!id || !api?.uploadMeetingAudioChunk) return
    void api.uploadMeetingAudioChunk(id, blob, seq, filename).catch(() => {
      // Best-effort archive — live captions still work without S3 chunks.
    })
  }

  private handleRecorderError = (code: string) => {
    this.set({ error: code })
    // A permanent server transcription failure will recur on every chunk.
    // Stop the recorder (releases the mic + ends the doomed upload loop)
    // and clear the meeting timers; the panel renders the reason.
    if (PERMANENT_TRANSCRIBE_ERRORS.has(code)) {
      this.recorder?.stop()
      this.clearTimers()
      this.unguardBeforeUnload()
      clearPersistedMeeting()
      this.set({ status: 'ended', isRecording: false })
    }
  }

  private async startRecorder() {
    const api = this.api
    this.recorder = createMeetingRecorder({
      transcribe: api?.transcribeAudio ?? (async () => ''),
      onSegment: this.handleSegmentText,
      onAudioChunk: this.handleAudioChunk,
      onError: this.handleRecorderError,
      onRecordingChange: (rec) => this.set({ isRecording: rec }),
      onTranscribingChange: (t) => this.set({ isTranscribing: t }),
      language: this.language,
    })
    await this.recorder.start()
  }

  private goLive(
    meeting: BoardMeeting,
    boardId: string,
    language: string | undefined,
  ) {
    this.meetingId = meeting.id
    this.language = language
    this.afterSeq = 0
    this.decidedIds.clear()
    this.set({
      status: 'live',
      meeting,
      boardId,
      segments: [],
      suggestions: [],
      mentionedCardIds: [],
    })
    persistMeeting({ meetingId: meeting.id, boardId, language })
    this.guardBeforeUnload()
  }

  /**
   * The server ended the meeting out from under this tab (remote Stop or
   * stale auto-end). Mirror the local wind-down of stop() WITHOUT calling
   * api.stopMeeting (already ended server-side), keeping a brief poll so
   * diarization status transitions still surface in the panel.
   */
  private handleRemoteEnd() {
    this.recorder?.stop()
    this.recorder = null
    this.clearTimers()
    this.unguardBeforeUnload()
    clearPersistedMeeting()
    this.set({ status: 'ended', isRecording: false })
    this.pollTimer = setInterval(() => { void this.poll() }, this.pollMs)
    this.postStopTimeout = setTimeout(() => {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    }, 120_000)
  }

  // ---- public lifecycle ----

  /**
   * Start a meeting — or ADOPT the one already live in this singleton, or
   * RESUME a persisted live meeting after a refresh. Idempotent while
   * live/starting so panel remounts on navigation never double-start.
   */
  async start(args: MeetingStartArgs): Promise<void> {
    const api = this.api
    if (!api?.startMeeting || this.isActive()) return
    this.pollMs = args.pollMs ?? POLL_MS
    this.analyzeMs = args.analyzeMs ?? ANALYZE_MS
    this.set({ error: null, status: 'starting' })

    // Refresh-resume: a live meeting persisted by THIS tab pre-refresh.
    const persisted = readPersistedMeeting()
    if (persisted && api.getMeetingLive) {
      let payload: Awaited<ReturnType<NonNullable<KanbanApi['getMeetingLive']>>> | null = null
      try {
        payload = await api.getMeetingLive(persisted.meetingId, 0)
      } catch {
        // The persisted meeting is gone server-side — fall through to a
        // fresh start below.
        payload = null
      }
      if (payload && payload.meeting.status === 'live') {
        const language = persisted.language ?? args.language
        // Slim live shape → BoardMeeting view (fields the panel reads).
        const meeting = {
          id: payload.meeting.id,
          board: persisted.boardId,
          status: payload.meeting.status,
          title: payload.meeting.title,
          language: language ?? '',
          transcript_text: '',
          last_analyzed_seq: payload.meeting.last_analyzed_seq,
          started_at: payload.meeting.started_at,
          ended_at: payload.meeting.ended_at,
          updated_at: payload.meeting.started_at,
          diarization_status: payload.meeting.diarization_status,
          diarized_transcript_text: payload.meeting.diarized_transcript_text,
          diarization_error: payload.meeting.diarization_error,
        } as BoardMeeting
        this.goLive(meeting, persisted.boardId, language)
        if (isRecorderSupported()) {
          try {
            await this.startRecorder()
          } catch (e) {
            // BUG-2026-07-06-MEETING-PANEL-SELF-OPENS — a mic failure
            // (NotAllowedError, no device) during resume must NOT be
            // swallowed into "fresh start": that silently began a brand-new
            // meeting that would also fail, and the persisted handoff left
            // behind re-opened the panel on EVERY board mount for 4 hours.
            // Invariant: any start() exit that is not a live recording
            // clears the persisted handoff.
            this.failStart(e, /* endServerMeeting */ true)
            return
          }
        }
        void this.poll()
        this.startTimers()
        return
      }
      clearPersistedMeeting()
    }

    let wentLive = false
    try {
      const m = await api.startMeeting({ board: args.boardId, language: args.language })
      this.goLive(m, args.boardId, args.language)
      wentLive = true
      await this.startRecorder()
      // Immediate first poll so a resumed meeting (existing segments +
      // suggestions) renders instantly instead of after one full interval.
      void this.poll()
      this.startTimers()
    } catch (e) {
      // goLive() already persisted the handoff; without cleanup a recorder
      // failure here left a stale sessionStorage entry + a live server
      // meeting, so the panel "randomly" re-opened on every navigation.
      this.failStart(e, /* endServerMeeting */ wentLive)
    }
  }

  /**
   * Unified failure exit for start(): release everything a partial start
   * acquired so a failed start can never leave a resumable ghost meeting.
   * Input: the thrown error + whether a server meeting went live and should
   * be best-effort ended. Output: idle state with the error surfaced.
   */
  private failStart(e: unknown, endServerMeeting: boolean): void {
    this.recorder?.stop()
    this.recorder = null
    this.clearTimers()
    this.unguardBeforeUnload()
    clearPersistedMeeting()
    const id = this.meetingId
    if (endServerMeeting && id && this.api?.stopMeeting) {
      // Fire-and-forget: the meeting would time out server-side anyway.
      void this.api.stopMeeting(id).catch(() => undefined)
    }
    this.meetingId = null
    this.set({
      ...IDLE_STATE,
      error: e instanceof Error ? e.message : 'start_failed',
    })
  }

  /** Explicit END — the only user path that stops a live recording. */
  async stop(): Promise<void> {
    this.recorder?.stop()
    this.recorder = null
    this.clearTimers()
    this.unguardBeforeUnload()
    clearPersistedMeeting()
    const id = this.meetingId
    const api = this.api
    this.set({ status: 'ended', isRecording: false })
    if (id && api?.stopMeeting) {
      try {
        const m = await api.stopMeeting(id)
        this.set({ meeting: m })
        // Keep polling briefly so diarization status transitions surface.
        this.pollTimer = setInterval(() => { void this.poll() }, this.pollMs)
        this.postStopTimeout = setTimeout(() => {
          if (this.pollTimer) {
            clearInterval(this.pollTimer)
            this.pollTimer = null
          }
        }, 120_000)
      } catch {
        // Non-fatal — the meeting will time out server-side regardless.
      }
    }
  }

  async accept(suggestionId: string): Promise<void> {
    const id = this.meetingId
    const api = this.api
    if (!id || !api?.acceptMeetingSuggestion) return
    // Mark decided FIRST so an in-flight poll resolving mid-call can't
    // re-add it (BUG-2026-06-29-MEETING-ACCEPT-DISMISS-REAPPEAR).
    this.decidedIds.add(suggestionId)
    this.set({ suggestions: this.state.suggestions.filter((s) => s.id !== suggestionId) })
    try {
      await api.acceptMeetingSuggestion(id, suggestionId)
    } catch (e) {
      this.decidedIds.delete(suggestionId)
      this.set({ error: e instanceof Error ? e.message : 'accept_failed' })
      void this.poll() // restore on failure
    }
  }

  async dismiss(suggestionId: string): Promise<void> {
    const id = this.meetingId
    const api = this.api
    if (!id || !api?.dismissMeetingSuggestion) return
    this.decidedIds.add(suggestionId)
    this.set({ suggestions: this.state.suggestions.filter((s) => s.id !== suggestionId) })
    try {
      await api.dismissMeetingSuggestion(id, suggestionId)
    } catch (e) {
      this.decidedIds.delete(suggestionId)
      this.set({ error: e instanceof Error ? e.message : 'dismiss_failed' })
      void this.poll()
    }
  }

  /** Test-only: hard reset the singleton between test cases. */
  __reset() {
    this.recorder?.stop()
    this.recorder = null
    this.clearTimers()
    this.unguardBeforeUnload()
    clearPersistedMeeting()
    this.meetingId = null
    this.contextCardId = null
    this.afterSeq = 0
    this.decidedIds.clear()
    this.pollInFlight = false
    this.analyzeInFlight = false
    this.pollMs = POLL_MS
    this.analyzeMs = ANALYZE_MS
    this.language = undefined
    this.api = null
    this.state = IDLE_STATE
    for (const fn of this.listeners) fn()
  }
}

const session = new MeetingSession()

export function getMeetingSession(): MeetingSession {
  return session
}

/**
 * True when the singleton has a live meeting OR a fresh persisted handoff
 * from a pre-refresh live meeting. useBoardUiPrefs seeds `meetingOpen` from
 * this so the panel re-mounts (and start() adopts/resumes) after any
 * navigation or refresh.
 */
export function hasActiveOrResumableMeeting(): boolean {
  return session.isActive() || readPersistedMeeting() !== null
}

/**
 * Card-context signal (D2): CardDetailDrawer calls this with the open card's
 * id (and `null` on close). Safe to call with no live meeting — the value is
 * only read when a segment is ingested.
 */
export function setMeetingContextCard(cardId: string | null): void {
  session.setContextCard(cardId)
}

/** Test-only alias so test files don't reach into the class. */
export function __resetMeetingSessionForTests(): void {
  session.__reset()
}
